import api from './api';

const SESSION_EXPIRED_KEY = 'sessionExpired';
const LOGOUT_REASON_KEY = 'logoutReason';

/**
 * Decodes the JWT payload (base64url) and checks the exp claim.
 * Does NOT verify the signature — signature verification is the backend's job.
 * Used only to proactively redirect before making an API call with a stale token.
 */
export function isTokenExpired(token) {
  if (!token) return true;
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}

const authService = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data));
      // RC17.6.1 root-cause fix — this used to be fire-and-forget, which raced
      // the caller's immediate post-login navigate(): Layout could mount and
      // read hasPermission() against an empty cache before this fetch landed,
      // then never re-render since hasPermission() is a plain synchronous read,
      // not reactive state. Awaiting here guarantees the cache is populated
      // before login() resolves and the caller navigates.
      await authService.fetchAndCachePermissions();
    }
    return response.data;
  },

  register: async (username, email, password, roles) => {
    const response = await api.post('/auth/register', {
      username,
      email,
      password,
      roles,
    });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response.data;
  },

  /**
   * Centralized logout.
   *
   * reason:
   *   'manual'   — user clicked Logout; no session-expired message shown
   *   'idle'     — auto-logout after inactivity timeout
   *   'expired'  — JWT expired (detected proactively or via 401)
   *   'disabled' — backend returned USER_DISABLED on a valid token
   */
  logout: (reason = 'manual') => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');

    if (reason !== 'manual') {
      sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true');
      sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
    }

    window.location.href = '/login';
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  /**
   * Fetches the caller's own effective permissions from the backend
   * (GET /api/auth/me/permissions) and caches them in localStorage under
   * 'permissions'. Called after login(); also safe to call standalone
   * (e.g. to refresh the cache after a permission-group change).
   *
   * Returns the fetched array, or null if the request failed (cache is left
   * untouched on failure so a transient error doesn't wipe out a good cache).
   */
  fetchAndCachePermissions: async () => {
    try {
      const response = await api.get('/auth/me/permissions');
      localStorage.setItem('permissions', JSON.stringify(response.data || []));
      return response.data;
    } catch {
      return null;
    }
  },

  /**
   * Synchronous permission check against the cached permissions array.
   * RC14.0 — replaces frontend `roles.includes('ADMIN' | 'SUPER_ADMIN')` checks
   * now that the backend is permission-only (no role fallback). Scope is not
   * considered here (a simple "has this code at all" boolean) — call sites that
   * need minimum-scope awareness use hasPermissionAtScope below instead.
   */
  hasPermission: (code) => {
    try {
      const cached = localStorage.getItem('permissions');
      if (!cached) return false;
      const permissions = JSON.parse(cached);
      if (!Array.isArray(permissions)) return false;
      return permissions.some((p) => p.permissionCode === code);
    } catch {
      return false;
    }
  },

  /**
   * RC19.1 — scope-aware permission check, e.g. gating the "Team Tasks" tab on holding
   * Task.View at TEAM scope or wider (not just OWN). `/api/auth/me/permissions` already returns
   * each grant's resolved scope (see EffectivePermission on the backend); this just reads it out
   * of the same cached array hasPermission uses, ordered the same way PermissionScope is on the
   * backend so "at least this scope" comparisons match server-side canAtScope exactly.
   */
  hasPermissionAtScope: (code, minScope) => {
    const SCOPE_ORDER = ['OWN', 'TEAM', 'DEPARTMENT', 'LOCATION', 'ORGANIZATION'];
    try {
      const cached = localStorage.getItem('permissions');
      if (!cached) return false;
      const permissions = JSON.parse(cached);
      if (!Array.isArray(permissions)) return false;
      const minIndex = SCOPE_ORDER.indexOf(minScope);
      return permissions.some((p) =>
        p.permissionCode === code && SCOPE_ORDER.indexOf(p.scope) >= minIndex);
    } catch {
      return false;
    }
  },

  /**
   * RC18.6.2 — true if the caller holds ANY of the given codes. Several tenant-administration
   * surfaces (Security Groups, Permission Profiles) now accept either the original
   * Platform-Operator-only code or its RC18.6.2 tenant-scoped counterpart (e.g.
   * `SecurityGroup.View` or `TenantSecurityGroup.View`) — same shape as the backend's
   * `@PreAuthorize(... or ...)` OR-clause.
   */
  hasAnyPermission: (...codes) => codes.some((code) => authService.hasPermission(code)),

  /**
   * Reads and clears the session-expired flag set by logout(reason).
   * Returns a user-facing message string, or null if no flag is set.
   * Call once on Login page mount.
   */
  getSessionExpiredMessage: () => {
    const expired = sessionStorage.getItem(SESSION_EXPIRED_KEY);
    if (!expired) return null;

    const reason = sessionStorage.getItem(LOGOUT_REASON_KEY);
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    sessionStorage.removeItem(LOGOUT_REASON_KEY);

    switch (reason) {
      case 'idle':
        return 'Your session expired due to inactivity.';
      case 'disabled':
        return 'Your account has been disabled. Contact your administrator.';
      case 'expired':
      default:
        return 'Your session has expired. Please sign in again.';
    }
  },
};

export default authService;
